<template>
  <div id="dt-viewer" ref="el">
    <View :preview-mode="previewMode">
      <Transition :name="slideTransitionName" v-if="isShowingStep" appear>
        <Slide
          v-model="slideTransitionName"
          :decision-tree="decisionTree"
          :current-node="currentNode"
          :current-node-view="currentNodeView"
          :key="currentNode.id"
        />
      </Transition>
    </View>
    <div v-if="debugMode">
      <hr />
      <h3>Debug Info Context</h3>
      <code>
        <ul>
          <li>
            <strong>Current Node Type</strong>
            <div>
              <pre>
                {{ currentNode.name }}
              </pre>
            </div>
          </li>
          <li v-if="isAllowBooking">
            <strong>Final Visit Type</strong>
            <div>
              <pre>
                {{ JSON.stringify(decisionTree.bookingVisitType) }}
              </pre>
            </div>
          </li>
          <li v-for="(value, key) in decisionTree.context" :key="key">
            <strong>{{ key }}</strong>
            <div>
              <pre>
                {{ JSON.stringify(value) }}
              </pre>
            </div>
          </li>
        </ul>
      </code>
    </div>
  </div>
</template>

<script setup>
import { useDecisionTreeStore } from "../store/decision-tree";

import View from "../components/View.vue";
import Slide from "../components/Slide.vue";

import StartNode from "../components/Node/StartNode.vue";
import InfoNode from "../components/Node/InfoNode.vue";
import SelectPatientTypeNode from "../components/Node/SelectPatientTypeNode.vue";
import AgeValidationNode from "../components/Node/AgeValidationNode.vue";
import SelectVisitTypeNode from "../components/Node/SelectVisitTypeNode.vue";
import SelectStateNode from "../components/Node/SelectStateNode.vue";
import QuestionNode from "../components/Node/QuestionNode.vue";
import DenyNode from "../components/Node/DenyNode.vue";
import ContinueNode from "../components/Node/ContinueNode.vue";
import MissingVisitTypeNode from "../components/Node/MissingVisitTypeNode.vue";

import { ref, computed, watch, onMounted, onUnmounted } from "vue";

defineProps({
  previewMode: {
    type: Boolean,
    default: false,
  },
});

const nodeComponents = {
  StartNode,
  InfoNode,
  SelectPatientTypeNode,
  AgeValidationNode,
  SelectVisitTypeNode,
  SelectStateNode,
  QuestionNode,
  DenyNode,
  ContinueNode,
  MissingVisitTypeNode,
};

const el = ref(null);

const slideTransitionName = ref("");

const { decisionTree, debugMode } = useDecisionTreeStore();

const currentNode = computed(() => decisionTree.currentNode);
const currentNodeView = computed(() => {
  const nodeName = currentNode.value.name;

  if (!(nodeName in nodeComponents)) {
    throw new Error(`Unknown node type: ${nodeName}`);
  }

  return nodeComponents[nodeName];
});

const isShowingStep = computed(() => decisionTree.isShowingStep);
const isAllowBooking = computed(() => decisionTree.isAllowBooking);
const isDeny = computed(() => decisionTree.isDeny);

watch(currentNode, async () => {
  const event = new CustomEvent("decision-tree:progress", {
    bubbles: true,
    detail: {
      value: decisionTree.progressPercent,
      currentState: decisionTree.currentState,
    },
  });

  el.value.dispatchEvent(event);
});

watch(isAllowBooking, async (newValue) => {
  if (!newValue) {
    return;
  }

  const event = new CustomEvent("decision-tree:allow-booking", {
    bubbles: true,
    detail: {
      patientType: decisionTree.bookingPatientType,
      visitTypeId: decisionTree.bookingVisitTypeId,
      switchProviderGroupIds: decisionTree.bookingProviderGroupIds,
      ageRange: decisionTree.bookingAgeRange,
      currentState: decisionTree.currentState,
      reviewStepBroadcasts: decisionTree.reviewStepBroadcasts,
      bookingMetadata: {
        confirmationPageBroadcasts: decisionTree.confirmationPageBroadcasts,
      },
    },
  });

  el.value.dispatchEvent(event);
});

watch(isDeny, async (newValue) => {
  if (!newValue) {
    return;
  }

  const event = new CustomEvent("decision-tree:deny", {
    bubbles: true,
    detail: {
      currentNodeId: decisionTree.currentNodeId,
    },
  });

  el.value.dispatchEvent(event);
});

function onPopState(event) {
  if (event.state.namespace === "decision-tree-nav") {
    if (decisionTree.prevNodeId === event.state.currentNodeId) {
      slideTransitionName.value = "slide-prev";
      decisionTree.goToPrevNode();
    } else if (decisionTree.nextNodeId === event.state.currentNodeId) {
      slideTransitionName.value = "slide-next";
      decisionTree.goToNextNode();
    }
  } else if (decisionTree.nextNodeId) {
    // Handle transition to next step of ABW
    decisionTree.goToNextNode();
  }
}

onMounted(() => {
  if (history.state?.namespace !== "decision-tree-nav") {
    history.replaceState({ namespace: "decision-tree-nav", currentNodeId: decisionTree.currentNode.id }, null);
  }

  window.addEventListener("popstate", onPopState);
});

onUnmounted(() => {
  window.removeEventListener("popstate", onPopState);
});
</script>
